import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { AuthService } from './auth.service';
import { FirestoreService } from './firestore.service';
import { EstadisticasService } from './estadisticas.service';
import { Timestamp } from '@angular/fire/firestore';

@Injectable({ providedIn: 'root' })
export class EvaluarService {
  private auth = inject(AuthService);
  private firestore = inject(FirestoreService);
  private estadisticasService = inject(EstadisticasService);
  private _puedeMap = new Map<string, WritableSignal<boolean>>();

  puedeEvaluarSignal(uidEvaluado: string): WritableSignal<boolean> {
    const miUid = this.auth.uid!;
    if (!this._puedeMap.has(uidEvaluado)) {
      // inicializamos con false (o true según prefieras)
      const sig = signal<boolean>(false);
      this._puedeMap.set(uidEvaluado, sig);

      // escuchamos en tiempo real
      this.firestore.listenDoc(
        `usuarios/${uidEvaluado}/evaluacionesRecibidas`,
        miUid,
        doc => {
          let can = true;
          if (doc?.["fecha"]) {
            const ahora = Date.now();
            const ultima = doc["fecha"].toDate().getTime();
            const horas = (ahora - ultima) / (1000 * 60 * 60);
            can = horas >= 72;
          }
          sig.set(can);
        }
      );
    }
    return this._puedeMap.get(uidEvaluado)!;
  }

  async guardarEvaluacion(uidEvaluado: string, data: Record<string, number>): Promise<void> {
    try {
      const uid = this.auth.uid;
      if (!uid || !uidEvaluado) throw new Error('No autorizado');

      const evaluacion = {
        ...data,
        fecha: Timestamp.now(),
        origen: 'chat'
      };

      await this.firestore.setDoc(
        `usuarios/${uidEvaluado}/evaluacionesRecibidas`,
        uid,
        evaluacion
      );

      await this.estadisticasService.incrementarCampo('cantidadTotalEvaluaciones', 1);
    } catch (error) {
      console.error('[EvaluarService] Error al guardar evaluación:', error);
      throw error;
    }
  }

  private calcularPromedioLocal(data: Record<string, number>): number {
    const valores = Object.values(data);
    if (!valores.length) return 0;
    return valores.reduce((a, b) => a + b, 0) / valores.length;
  }

  async obtenerEvaluaciones(uid: string): Promise<Record<string, number>[]> {
    try {
      return await this.firestore.getCollection<Record<string, number>>(
        `usuarios/${uid}/evaluacionesRecibidas`
      );
    } catch (error) {
      console.error('[EvaluarService] Error al obtener evaluaciones:', error);
      return [];
    }
  }

  async obtenerPromedios(uid: string): Promise<Record<string, number>> {
    try {
      const col = await this.firestore.getCollection<any>(`usuarios/${uid}/evaluacionesRecibidas`);
      const acumulador: Record<string, number[]> = {};

      for (const evaluacion of col) {
        for (const [key, val] of Object.entries(evaluacion)) {
          if (typeof val === 'number') {
            acumulador[key] ??= [];
            acumulador[key].push(val);
          }
        }
      }

      const promedios: Record<string, number> = {};
      for (const key of Object.keys(acumulador)) {
        const valores = acumulador[key];
        const promedio = valores.reduce((a, b) => a + b, 0) / valores.length;
        promedios[key] = Math.round(promedio * 10) / 10;
      }
      return promedios;
    } catch (error) {
      console.error('[EvaluarService] Error al calcular promedios:', error);
      return {};
    }
  }

  async obtenerConteoPorCampo(uid: string): Promise<Record<string, number>> {
    try {
      const col = await this.firestore.getCollection<any>(`usuarios/${uid}/evaluacionesRecibidas`);
      const conteo: Record<string, number> = {};

      for (const evaluacion of col) {
        for (const [key, val] of Object.entries(evaluacion)) {
          if (typeof val === 'number') {
            conteo[key] ??= 0;
            conteo[key]++;
          }
        }
      }

      return conteo;
    } catch (error) {
      console.error('[EvaluarService] Error al obtener conteo por campo:', error);
      return {};
    }
  }

  async ultimasEvaluaciones(uid: string, limite: number = 3): Promise<any[]> {
    try {
      const col = await this.firestore.getCollection<any>(`usuarios/${uid}/evaluacionesRecibidas`);

      return col
        .filter(e => e.fecha)
        .sort((a, b) => {
          const fechaA = a.fecha?.seconds ?? 0;
          const fechaB = b.fecha?.seconds ?? 0;
          return fechaB - fechaA;
        })
        .slice(0, limite);
    } catch (error) {
      console.error('[EvaluarService] Error al obtener últimas evaluaciones:', error);
      return [];
    }
  }
}
